import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { SUBTYPE } from '../../constants';
import MenuChannel from './MenuChannel';
import MenuItem from './MenuItem';
import MenuSubheader from './MenuSubheader';
import {
  openChannelCreator,
  openSubredditSearcher,
  showMessage,
  signOut,
  switchSubscription,
} from '../../actions';
import { selectChannels, setChannels } from '../../store/channel/channelSlice';
import {
  selectSubreddits,
  setSubreddits,
} from '../../store/subreddit/subredditSlice';

import './Menu.css';

/**
 * App Menu
 */
function MainMenu({ selection, username, reddit }) {
  const dispatch = useDispatch();
  const subreddits = useSelector(selectSubreddits);
  const channels = useSelector(selectChannels);

  useEffect(() => {
    reddit
      .getSubscriptions()
      .then(data => {
        dispatch(setSubreddits(parseSubscriptions(data)));
      })
      .catch(error => {
        showMessage(`Error getting subscribed subreddits: ${error}`);
      });

    reddit
      .getMyMultireddits()
      .then(data => {
        dispatch(setChannels(parseMultireddits(data)));
      })
      .catch(error => {
        showMessage(`Error getting subscribed channels: ${error}`);
      });
  }, [dispatch, reddit]);

  const selectedType = selection.type || 0;
  const selectedName = selection.name || '';

  return (
    <div className="Menu">
      <MenuSubheader name={username || 'Username'} />
      <MenuItem name="Sign Out" onClick={signOut} />

      <MenuItem
        name="Saved Posts"
        selected={selectedType === SUBTYPE.SAVED_POSTS}
        onClick={() => {
          switchSubscription({
            type: SUBTYPE.SAVED_POSTS,
            name: 'Saved Posts',
          });
        }}
      />

      <MenuSubheader
        name="Subreddits"
        onActionRequest={openSubredditSearcher}
      />

      <MenuItem
        name="frontpage"
        selected={
          selectedType === SUBTYPE.SUBREDDIT && selectedName === 'frontpage'
        }
        onClick={() => {
          switchSubscription({ type: SUBTYPE.SUBREDDIT, name: 'frontpage' });
        }}
      />

      <MenuItem
        name="popular"
        selected={
          selectedType === SUBTYPE.SUBREDDIT && selectedName === 'popular'
        }
        onClick={() => {
          switchSubscription({ type: SUBTYPE.SUBREDDIT, name: 'popular' });
        }}
      />

      <MenuItem
        name="all"
        selected={selectedType === SUBTYPE.SUBREDDIT && selectedName === 'all'}
        onClick={() => {
          switchSubscription({ type: SUBTYPE.SUBREDDIT, name: 'all' });
        }}
      />

      {subreddits
        .slice()
        .sort((a, b) => a.localeCompare(b))
        .map(subreddit => {
          const isSelected =
            selectedType === SUBTYPE.SUBREDDIT && selectedName === subreddit;
          return (
            <MenuItem
              name={subreddit}
              key={subreddit}
              selected={isSelected}
              onClick={name => {
                switchSubscription({ type: SUBTYPE.SUBREDDIT, name });
              }}
            />
          );
        })}

      <MenuSubheader name="Channels" onActionRequest={openChannelCreator} />

      {channels.map(channel => {
        const isSelected =
          selectedType === SUBTYPE.CHANNEL &&
          selectedName.toLowerCase() === channel.name.toLowerCase();
        return (
          <MenuChannel
            channel={channel}
            key={channel.name}
            selected={isSelected}
            onNavigation={() => {
              switchSubscription({
                type: SUBTYPE.CHANNEL,
                name: channel.name,
              });
            }}
          />
        );
      })}
    </div>
  );
}

MainMenu.propTypes = {
  selection: PropTypes.shape({
    type: PropTypes.string,
    name: PropTypes.string,
  }),
  username: PropTypes.string.isRequired,
  reddit: PropTypes.object.isRequired,
};

export default MainMenu;

const parseSubscriptions = subscriptionsData =>
  Array.from(subscriptionsData, subscription => subscription.display_name);

const parseMultireddits = multisData =>
  Array.from(multisData, multi => {
    return {
      name: multi.display_name,
      subreddits: Array.from(multi.subreddits, sub => sub.display_name),
    };
  });
